import SimpleTrack from '@common/types/release/simple-track';
import { TracksState } from './state';
import { trackInitialState } from './initial-state';
import { TrackActions } from './action-creators';
import * as actions from './actions';
import { lastTrackNumber } from './selectors';

export const tracksReducer = (
  state: TracksState = trackInitialState,
  action: TrackActions
): TracksState => {
  switch (action.type) {
    case actions.NEW_TRACK: {
      return {
        ...state,
        tracks: {
          ...state.tracks,
          [action.track.id as string]: {
            ...action.track,
            artists: [
              {
                ...action.artist,
                role: 'main',
              },
            ],
            order:
              lastTrackNumber(state.tracks, action.track.releaseId as string) +
              1,
          } as SimpleTrack,
        },
      };
    }

    case actions.CREATE_TRACK: {
      return {
        ...state,
        tracks: {
          ...state.tracks,
          [action.trackId]: {
            ...state.tracks[action.trackId],
            releaseId: action.releaseId,
          },
        },
        disableReplicateCollaborators: !Object.keys(state.tracks).length
          ? false
          : state.disableReplicateCollaborators,
      };
    }
    case actions.UPDATE_INVALID_FORM: {
      return {
        ...state,
        invalidForm: action.value,
      };
    }
    case actions.UPDATE_TRACK_SUCCESS: {
      return {
        ...state,
        tracks: {
          ...state.tracks,
          [action.track.id as string]: {
            ...state.tracks[action.track.id as string],
            status: action.track.status,
          },
        },
        updateStatus: 'saved',
      };
    }

    case actions.UPDATE_TRACK_ORDER: {
      const newState: TracksState = {
        ...state,
        updateStatus: 'modified',
      };

      action.orderUpdates.forEach(({ id, order }) => {
        if (!(id in newState.tracks)) {
          return;
        }
        newState.tracks[id] = {
          ...newState.tracks[id],
          order,
        };
      });

      const sortedTracks = Object.values(newState.tracks).sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );

      return {
        ...newState,
        tracks: Object.fromEntries(
          sortedTracks.map((t) => [t.id as string, { ...t }])
        ),
      };
    }

    case actions.UPDATE_TRACK_ORDER_SUCCESS: {
      return {
        ...state,
        updateStatus: 'saved',
      };
    }

    case actions.GET_TRACKS_SUCCESS: {
      const newState: TracksState = {
        ...state,
        tracks: {},
      };

      action.tracks.forEach((track) => {
        newState.tracks[track.id as string] = track;
      });

      return newState;
    }

    case actions.REMOVE_TRACK: {
      const newState: TracksState = {
        ...state,
        tracks: {},
      };

      let last = 0;

      Object.values(state.tracks)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .forEach((t) => {
          if (t.id === action.trackId || !t.id) {
            return;
          }
          last += 1;

          newState.tracks[t.id as string] = {
            ...t,
            order: last,
          };
        });

      return newState;
    }

    case actions.UPDATE_TRACK_FIELD:
      return {
        ...state,
        tracks: {
          ...state.tracks,
          [action.trackId]: {
            ...state.tracks[action.trackId],
            video:
              action.field === 'hasMusicVideo' && action.value === false
                ? undefined
                : state.tracks[action.trackId]
                ? state.tracks[action.trackId].video
                : undefined,
            [action.field]: action.value,
          },
        },
        updateStatus: 'modified',
      };

    case actions.UPDATE_TRACK_PREVIEW:
      return {
        ...state,
        tracks: {
          ...state.tracks,
          [action.trackId]: {
            ...state.tracks[action.trackId],
            previewStart: action.previewStart,
            previewLength: action.previewLength,
          },
        },
        updateStatus: 'modified',
      };

    case actions.DISABLE_REPLICATE_COLLABORATORS:
      return {
        ...state,
        disableReplicateCollaborators: true,
      };

    case actions.RESET_TRACKS_STORE:
      return { ...trackInitialState };

    default: {
      return { ...state };
    }
  }
};
